import { useMutation } from '@apollo/client/react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from 'es-toolkit';
import { Button, Modal } from '@insio/ui-toolkit';
import { UserConsentSource } from '@insio/shared-types';
import { AcceptPoliciesDocument, SetGdprConsentDocument } from '@/graphql';
import { HIDE_CLOSE_BTN } from '@/components/modal-chrome';
import { useAuth } from '../context';
import { useSignOut } from '../hooks';
import { getGdprConsentText } from '../content/gdpr-consent-content';
import { isConsentPending } from '../utils/consent';
import { PoliciesConsentCheckbox } from './PoliciesConsentCheckbox';
import { GdprConsentCheckbox } from './GdprConsentCheckbox';

type ConsentStepId = 'policies' | 'gdpr';

interface ConsentAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface ConsentStepProps {
  title: string;
  indicator: ReactNode;
  primary: ConsentAction;
  ghost: ConsentAction;
  children: ReactNode;
}

/** Shared chrome for one step of the gate: header, body, primary + ghost action. */
function ConsentStep({ title, indicator, primary, ghost, children }: ConsentStepProps) {
  return (
    <>
      <Modal.Header onClose={noop} className={HIDE_CLOSE_BTN}>
        <div className='flex w-full items-center justify-between gap-md'>
          <span className='text-lg font-medium'>{title}</span>
          {indicator}
        </div>
      </Modal.Header>

      <Modal.Content className='flex flex-col gap-lg p-lg sm:p-xl'>{children}</Modal.Content>

      <Modal.Footer className='flex-col! items-stretch!'>
        <Modal.Action>
          <Button
            variant='primary'
            size='md'
            onClick={primary.onClick}
            disabled={primary.disabled}
            className='w-full'
          >
            {primary.label}
          </Button>
        </Modal.Action>
        <Button
          variant='ghost'
          size='sm'
          onClick={ghost.onClick}
          disabled={ghost.disabled}
          className='w-full text-sm text-foreground-muted hover:text-foreground-base'
        >
          {ghost.label}
        </Button>
      </Modal.Footer>
    </>
  );
}

export interface ConsentGateModalProps {
  /** Collect only the policies now; the GDPR decision is asked after onboarding. */
  deferGdpr?: boolean;
}

/**
 * Mandatory, non-dismissable modal that gates the app until the user has accepted the
 * current Terms of Service + Privacy Policy and decided on the GDPR marketing consent.
 * Starts on the GDPR step when the policies are already accepted — the common case.
 */
export function ConsentGateModal({ deferGdpr = false }: ConsentGateModalProps) {
  const { t, i18n } = useTranslation();
  const { user, refetch } = useAuth();
  const { signOut, loading: signingOut } = useSignOut();

  // Both steps only show when the policies are outstanding, which is rare — a
  // version bump. Otherwise the modal is a single GDPR step.
  const [policiesPending] = useState(() => isConsentPending(user));
  const twoStep = policiesPending && !deferGdpr;
  const [step, setStep] = useState<ConsentStepId>(policiesPending ? 'policies' : 'gdpr');

  const [policiesConsent, setPoliciesConsent] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [acceptPolicies, { loading: accepting }] = useMutation(AcceptPoliciesDocument);
  const [setGdprDecision, { loading: deciding }] = useMutation(SetGdprConsentDocument);

  /**
   * Accepts the policies and advances locally. Refetching here would unmount the
   * modal mid-flow, so the single refetch happens after the GDPR decision — unless
   * that decision is deferred, when the refetch closes the gate for onboarding.
   */
  const handleAcceptPolicies = async () => {
    await acceptPolicies();
    if (twoStep) setStep('gdpr');
    else await refetch();
  };

  const decideGdpr = async (accepted: boolean) => {
    await setGdprDecision({ variables: { accepted, source: UserConsentSource.GATE } });
    await refetch();
  };

  const indicator = twoStep ? (
    <span className='text-sm text-foreground-muted'>
      {t('features.auth.consentGateModal.stepIndicator', {
        current: step === 'policies' ? 1 : 2,
        total: 2
      })}
    </span>
  ) : null;

  return (
    <Modal open onClose={noop} size='sm' layout='vertical'>
      {step === 'policies' && (
        <ConsentStep
          title={t('features.auth.policiesConsentModal.title')}
          indicator={indicator}
          primary={{
            label: t('features.auth.policiesConsentModal.continue'),
            onClick: handleAcceptPolicies,
            disabled: !policiesConsent || accepting || signingOut
          }}
          ghost={{
            label: t('features.auth.policiesConsentModal.logout'),
            onClick: signOut,
            disabled: accepting || signingOut
          }}
        >
          <p className='text-sm text-foreground-muted'>
            {t('features.auth.policiesConsentModal.description')}
          </p>
          <PoliciesConsentCheckbox checked={policiesConsent} onChange={setPoliciesConsent} />
        </ConsentStep>
      )}

      {step === 'gdpr' && (
        <ConsentStep
          title={t('features.auth.gdprConsentModal.title')}
          indicator={indicator}
          primary={{
            label: t('features.auth.gdprConsentModal.accept'),
            onClick: () => decideGdpr(true),
            disabled: !gdprConsent || deciding
          }}
          ghost={{
            label: t('features.auth.gdprConsentModal.refuse'),
            onClick: () => decideGdpr(false),
            disabled: deciding
          }}
        >
          <GdprConsentText language={i18n.language} />
          <GdprConsentCheckbox checked={gdprConsent} onChange={setGdprConsent} />
        </ConsentStep>
      )}
    </Modal>
  );
}

/** Renders the versioned consent markdown: `# ` lines as headings, the rest as paragraphs. */
function GdprConsentText({ language }: { language: string }) {
  const blocks = useMemo(
    () =>
      getGdprConsentText(language)
        .split(/\n\s*\n/)
        .map(block => block.trim())
        .filter(Boolean),
    [language]
  );

  return (
    <div className='flex flex-col gap-md max-h-[40vh] pb-lg overflow-y-auto'>
      {blocks.map((block, index) =>
        block.startsWith('# ') ? (
          <h3 key={index} className='text-base font-semibold text-foreground'>
            {block.slice(2)}
          </h3>
        ) : (
          <p key={index} className='text-sm leading-relaxed text-foreground-muted'>
            {block}
          </p>
        )
      )}
    </div>
  );
}
